/**
 * Component is used to show store's address based on its identifier.
 *
 * @attr {String} title-address - The title for address block
 * @attr {String} store-code - an identifier of a store
 * @attr {String} seo-tag - can be only h1-h6. Content will be wrapped in the selected tag
 * @prop {String} formattedAddress - an string of address
 *
 */

import { html } from 'lit-element';

import { SimpleLitElement } from '@elab/components';
import { actions, selectors } from '@elab/core/store/store-details';
import connect from '@elab/core/store/connect';
import { seoContentWrapper } from '@elab/core/utils/htmlTemplates';

export class StoreDetailsAddress extends SimpleLitElement {
    static get properties() {
        return {
            storeUid: { type: String, attribute: 'store-code' },
            titleAddress: { type: String, attribute: 'title-address' },
            notFormattedAddress: {
                type: Boolean,
                attribute: 'not-formatted-address',
            },
            showPhoneNumber: {
                type: Boolean,
                attribute: 'show-phone-number',
            },
            townStreetAddressStructure: {
                type: Boolean,
                attribute: 'town-street-address-structure',
            },
            formattedAddress: { type: String },
            line1: { type: String },
            line2: { type: String },
            postalCode: { type: String },
            town: { type: String },
            phone: { type: String },
            seoTag: { type: String, attribute: 'seo-tag' },
        };
    }

    constructor() {
        super();
        this.seoTag = '';
    }

    firstUpdated() {
        this.dispatchEvent(new CustomEvent('get-store-details'));
    }

    notFormattedAddressTemplate = () => html`
        <div class="address-card__address">
            <span class="address-card__address-lines">
                <span class="address-card__address--line1">${this.line1}</span>
                <span class="address-card__address--line2">${this.line2}</span>
            </span>
            <span class="address-card__address--postalCode">
                ${this.postalCode}
            </span>
            <span class="address-card__address--town">${this.town}</span>
        </div>
    `;

    townStreetAddressTemplate = () => html`
        <div class="address-card__address">
            <span class="address-card__address--town">
                ${this.town}
            </span>
            <span class="address-card__address--line1">${this.line1}</span>
        </div>
    `;

    formattedAddressTemplate = () => html`
        <div class="address-card__address">${this.formattedAddress}</div>
    `;

    formattedPhoneNumber = () => html`
        <div class="address-card__phone">${this.phone}</div>
    `;

    selectTemplate = () => {
        if (this.notFormattedAddress) {
            return this.notFormattedAddressTemplate();
        }
        if (this.townStreetAddressStructure) {
            return this.townStreetAddressTemplate();
        }
        if (!this.notFormattedAddress && !this.townStreetAddressStructure) {
            return this.formattedAddressTemplate();
        }
    };

    render() {
        return this.formattedAddress
            ? html`
                  <div class="address-card">
                      <span class="address-card__home icon"></span>
                      <span class="address-card__title">
                          ${seoContentWrapper(this.seoTag, `${this.titleAddress}:`)}
                      </span>
                      ${this.selectTemplate()}
                      ${this.showPhoneNumber ? this.formattedPhoneNumber() : html``}
                  </div>
              `
            : html``;
    }
}

const mapDispatchToEvents = (dispatch, ctx) => ({
    'get-store-details': () => dispatch(actions.getStoreDetails(ctx.storeUid)),
});

const mapStateToProps = state => {
    const { address = {} } = selectors.getDataStore(state) || {};
    return {
        formattedAddress: address.formattedAddress,
        line1: address.line1,
        line2: address.line2,
        postalCode: address.postalCode,
        town: address.town,
        phone: address.phone,
    };
};

export default () => {
    customElements.define(
        'e2-store-details-address',
        connect(mapStateToProps, mapDispatchToEvents)(StoreDetailsAddress),
    );
};
